import type { Custodian } from 'models/custodian';
import type { CostAndCharge } from 'models/streaming/costsAndCharges';
import type { UnderlyingCodeType } from 'models/underlying/base';
import { connect } from 'react-redux';
import type { BasketOptimizerResultRow } from 'state/advisory/basket-optimizer/model';
import { getResultRows } from 'state/advisory/basket-optimizer/selectors';
import type { DataById } from 'state/formData/formData.model';
import { getFormEnrichedData } from 'state/formData/formData.selectors';
import { getFieldsMetadata } from 'state/forms/metadata/selectors';
import type { GreeksTable } from 'state/quotes/details/details.model';
import type { IRegulatoryResource } from 'state/regulatoryDocuments';
import {
  getFormQuoteDetails,
  getQuoteRegulationInfos,
  getStaticFormInfos,
  getStaticQuoteResult,
  getStaticWorkflowStatus,
  getUserPreferencesLanguage,
  getUserPreferencesQuoteWorkspaceVersion,
  getUserPreferencesUnderlyingCodeType,
  isUserPreferencesDarkMode,
} from 'state/selectors';
import type { AppState } from 'state/state.model';
import type { QuoteWorkspaceVersion } from 'state/userPreferences/userPreferences.model';
import { getWorkspaceActiveTabId } from 'state/workspaces/workspaces/workspaces.selector';

export interface OwnProps {
  externalQuoteId?: string;
}

export interface StateProps {
  availableCustodians: Custodian[];
  formEnrichedData: DataById;
  isDarkMode: boolean;
  isSolved: boolean;
  solveResult?: number;
  tabId: string;
  kidDocumentation?: IRegulatoryResource[];
  productGovernanceDocumentation?: IRegulatoryResource[];
  optimizedBasketResults: BasketOptimizerResultRow[];
  costsAndChargesCurrency: string;
  costsAndChargesResults: CostAndCharge[];
  defaultUnderlyingCodeType: UnderlyingCodeType;
  languageCode?: string;
  quoteWorkspaceVersion?: QuoteWorkspaceVersion;
  mustForceAdvancedMode?: boolean;
  greeks?: GreeksTable;
  equityGreeks?: GreeksTable;
}

export interface DealSplitFieldMetadata {
  availableCustodians: Custodian[];
}

export type MailSenderProps = OwnProps & StateProps;

function isQuoteSolved(state: AppState, tabId: string): boolean {
  const workflowStatus = getStaticWorkflowStatus(state, tabId);
  const result =
    workflowStatus === 'QUOTED' ||
    workflowStatus === 'QUOTED_TRADABLE' ||
    workflowStatus === 'QUOTED_PREVIOUSLY' ||
    workflowStatus === 'TRADING' ||
    workflowStatus === 'TRADED';

  return result;
}

const mapStateToProps = (state: AppState): StateProps => {
  const tabId = getWorkspaceActiveTabId(state, 'static')!;
  const quote = getStaticQuoteResult(state, tabId);
  const isSolved = isQuoteSolved(state, tabId);
  const regulationInfos = getQuoteRegulationInfos(state, 'static', tabId);
  const fieldsMetadata = getFieldsMetadata(state, 'static', tabId);
  const dealSplitFieldMetadata = fieldsMetadata?.DealSplit as DealSplitFieldMetadata;
  const availableCustodians = dealSplitFieldMetadata?.availableCustodians ?? [];
  const kidDocumentation = regulationInfos?.kidDocument;
  const productGovernanceDocumentation = regulationInfos?.productGovernanceDocumentation;
  const optimizedBasketResults = getResultRows(state, tabId) ?? [];

  const quoteDetails = getFormQuoteDetails(state, 'static', tabId);

  const mustForceAdvancedMode = getStaticFormInfos(state, tabId)?.mustForceAdvancedMode ?? false;

  const costsAndCharges = regulationInfos?.costAndCharge;
  const costsAndChargesCurrency = quoteDetails?.Currency as string;

  const costsAndChargesResults = costsAndCharges || [];

  const defaultUnderlyingCodeType = getUserPreferencesUnderlyingCodeType(state);
  const languageCode = getUserPreferencesLanguage(state);
  const quoteWorkspaceVersion = getUserPreferencesQuoteWorkspaceVersion(state);
  const greeks = quoteDetails?.Greeks?.assetClass === 'Forex' ? quoteDetails?.Greeks : undefined;
  const equityGreeks = quoteDetails?.Greeks?.assetClass === 'Equity' ? quoteDetails?.Greeks : undefined;
  return {
    availableCustodians,
    formEnrichedData: getFormEnrichedData(state, tabId),
    isDarkMode: isUserPreferencesDarkMode(state),
    isSolved,
    solveResult: quote?.solveResult,
    tabId,
    kidDocumentation,
    productGovernanceDocumentation,
    optimizedBasketResults,
    costsAndChargesResults,
    costsAndChargesCurrency,
    defaultUnderlyingCodeType,
    languageCode,
    quoteWorkspaceVersion,
    mustForceAdvancedMode,
    greeks,
    equityGreeks,
  };
};

export const connectSummaryMailSender = connect(mapStateToProps);
